import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AgentActionService } from "../../services/AgentActionService";
import { ApiKeyOrGetter } from "../../services/GeneralService";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../ui/dropdown-menu";
import { MoreHorizontal, Loader2 } from "lucide-react";
import moment from "moment";
import PaginatedTable, { PaginatedTableColumn } from "../ui/paginated-table";

interface AgentActionVersionsTableProps {
  actionId: number;
  agentId: number;
  apiKey: ApiKeyOrGetter;
  isVersionView: boolean;
}

interface ActionVersion {
  id: number;
  version_number: string;
  version_notes: string;
  date_created: number;
  is_latest: boolean;
}

const AgentActionVersionsTable: React.FC<AgentActionVersionsTableProps> = ({
  actionId,
  agentId,
  apiKey,
  isVersionView
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [versions, setVersions] = useState<ActionVersion[]>([]);
  const [versionsLoading, setVersionsLoading] = useState<boolean>(true);
  const [draftingVersion, setDraftingVersion] = useState<string | null>(null);
  const [draftConfirmDialogOpen, setDraftConfirmDialogOpen] = useState<boolean>(false);
  const [pendingDraftVersion, setPendingDraftVersion] = useState<string | null>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState<number>(1);
  const pageSize = 10;

  // Track mounted state to avoid setState on unmounted component
  const isMountedRef = useRef<boolean>(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Load versions
  useEffect(() => {
    if (actionId && apiKey) {
      setVersionsLoading(true);
      const subscription = AgentActionService.getActionVersions(actionId, () => {}, apiKey).subscribe(
        (response: any) => {
          setVersions(response.versions || []);
          setVersionsLoading(false);
        },
        (error: any) => {
          console.error("Error loading action versions:", error);
          setVersionsLoading(false);
        }
      );
      return () => subscription.unsubscribe();
    }
  }, [actionId, apiKey]);

  const handleDraftFromVersion = (versionNumber: string) => {
    // Open confirmation dialog
    setPendingDraftVersion(versionNumber);
    setDraftConfirmDialogOpen(true);
  };

  const confirmDraftFromVersion = () => {
    if (!pendingDraftVersion) return;
    
    // Keep dialog open and show loading state
    setDraftingVersion(pendingDraftVersion);
    
    // Create draft FIRST, then navigate after it's ready
    AgentActionService.draftFromActionVersion(actionId, pendingDraftVersion, () => {}, apiKey).subscribe(
      () => {
        // Draft created successfully - backend already waited for hash computation
        // Reload version status immediately (backend already returned refreshed status)
        AgentActionService.getActionVersionStatus(actionId, () => {}, apiKey).subscribe(
          (res: any) => {
            if (!isMountedRef.current) return;
            // Dispatch event to notify parent component to reload version status
            window.dispatchEvent(new CustomEvent("actionVersionStatusUpdated", {
              detail: { actionId, versionStatus: res }
            }));
            // Close dialog and navigate only after success
            setDraftConfirmDialogOpen(false);
            setDraftingVersion(null);
            setPendingDraftVersion(null);
            navigate(`/agent/${agentId}/actions/${actionId}`);
          },
          (err: any) => {
            console.error("Error loading action version status:", err);
            if (!isMountedRef.current) return;
            // Close dialog on error
            setDraftConfirmDialogOpen(false);
            setDraftingVersion(null);
            setPendingDraftVersion(null);
          }
        );
      },
      (err: any) => {
        console.error("Error drafting from version:", err);
        if (!isMountedRef.current) return;
        // Close dialog on error
        setDraftConfirmDialogOpen(false);
        setDraftingVersion(null);
        setPendingDraftVersion(null);
      }
    );
  };

  // Define columns for the table
  const columns: PaginatedTableColumn<ActionVersion>[] = useMemo(() => [
    {
      key: "version_number" as keyof ActionVersion,
      header: "Version",
      width: "w-32",
      render: (version: ActionVersion) => (
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="font-mono">
            v{version.version_number}
          </Badge>
          {version.is_latest && (
            <Badge className="bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300 text-xs">
              Latest
            </Badge>
          )}
        </div>
      ),
    },
    {
      key: "version_notes" as keyof ActionVersion,
      header: "Description",
      render: (version: ActionVersion) => (
        <div className="max-w-md">
          <p className="text-sm text-foreground truncate">
            {version.version_notes || "No description provided"}
          </p>
        </div>
      ),
    },
    {
      key: "date_created" as keyof ActionVersion,
      header: "Created",
      width: "w-48",
      render: (version: ActionVersion) => (
        <span className="text-sm text-muted-foreground">
          {moment(version.date_created * 1000).format("MMM D, YYYY h:mm A")}
        </span>
      ),
    },
    {
      key: "id" as keyof ActionVersion, // Using id as key for actions column
      header: "",
      width: "w-12",
      render: (version: ActionVersion) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
              <MoreHorizontal className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onSelect={() => {
                const params = new URLSearchParams(searchParams);
                params.set("version", version.version_number);
                navigate(`/agent/${agentId}/actions/${actionId}?${params.toString()}`);
              }}
            >
              View
            </DropdownMenuItem>
            {!isVersionView && !version.is_latest && (
              <DropdownMenuItem
                onSelect={() => {
                  handleDraftFromVersion(version.version_number);
                }}
                disabled={draftingVersion !== null}
                className="text-maitai-green"
              >
                {draftingVersion === version.version_number ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Loading...
                  </>
                ) : (
                  "Draft From This"
                )}
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ], [actionId, agentId, draftingVersion, isVersionView, navigate, searchParams]);

  return (
    <>
      <PaginatedTable<ActionVersion>
        data={versions}
        columns={columns}
        loading={versionsLoading}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        pageSize={pageSize}
        emptyTitle="No versions published yet"
        emptyDescription="Publish your first version to get started."
        showPagination={versions.length > pageSize}
        loadingRows={5}
        defaultSort={(a, b) => b.date_created - a.date_created}
      />

      {/* Draft From Version Confirmation Dialog */}
      <AlertDialog 
        open={draftConfirmDialogOpen} 
        onOpenChange={(open) => {
          if (!open && draftingVersion === null) {
            setDraftConfirmDialogOpen(false);
            setPendingDraftVersion(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-gc-med">
              Create Draft from Version
            </AlertDialogTitle>
            <AlertDialogDescription>
              {draftingVersion ? (
                <div className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Creating draft and loading changes...
                </div>
              ) : (
                `Are you sure you want to create a draft from version ${pendingDraftVersion}? This will overwrite the current draft.`
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={draftingVersion !== null}>Cancel</AlertDialogCancel>
            <Button
              onClick={confirmDraftFromVersion}
              disabled={draftingVersion !== null}
              className="bg-maitai-green hover:bg-maitai-green/90 text-white"
            >
              {draftingVersion ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating Draft...
                </>
              ) : (
                "Create Draft"
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default AgentActionVersionsTable;
